import React from 'react';
import { useHistory } from 'react-router-dom';

import { usePageStore } from '@client/store/pageStore/context/pageContext';
import aggregateSpectrograms from '@client/utils/aggregateSpectrograms';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import { SpectrogramPoint } from '@ledium/types';
import { AutocompleteBordered, AutocompleteOptionWithChart } from '@ledium/ui-common';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Alert, AlertTitle, Box, Button, Container, Grid, Paper, TextField, Typography } from '@mui/material';

interface LightRecipeItem {
    id: string;
    name: string;
    description?: string;
    spectrogram: SpectrogramPoint[];
}

interface SpectrumMixOption {
    id: string;
    name: string;
    description?: string;
    groupId?: string;
    linkedReason?: string;
    linked?: { projectId: string; projectName: string } | null;
    lightChannels: Array<{ spectrogram: SpectrogramPoint[]; intensityPercent: number }>;
    lightRecipe?: { spectrogram?: SpectrogramPoint[] } | null;
}

interface ProjectEditorSpectrumMixSelectorProps {
    lightRecipeId: string;
    spectrumMixId: string | null;
    onSpectrumMixSelect: (lightRecipeId: string, spectrumMixId: string | null) => void;
    readOnly: boolean;
    projectId: unknown;
}

const ProjectEditorSpectrumMixSelector = ({
    lightRecipeId,
    spectrumMixId,
    onSpectrumMixSelect,
    readOnly,
    projectId
}: ProjectEditorSpectrumMixSelectorProps) => {
    const history = useHistory();
    const {
        pageState: { formData }
    } = usePageStore();
    const [lightRecipe, setLightRecipe] = React.useState<LightRecipeItem | null>(null);
    const [spectrumMixListForRecipe, setSpectrumMixListForRecipe] = React.useState<SpectrumMixOption[]>([]);
    const [matchedSpectrumMixList, setMatchedSpectrumMixList] = React.useState<SpectrumMixOption[]>([]);
    const [selectedSpectrumMix, setSelectedSpectrumMix] = React.useState<SpectrumMixOption | null>(null);

    React.useEffect(() => {
        lediumApi
            .get<{ spectrumMixList: SpectrumMixOption[]; lightRecipe: LightRecipeItem }>(`spectrumMixes/filterByLightRecipe/${lightRecipeId}`)
            .then((data) => {
                if (!data) {
                    return;
                }
                setSpectrumMixListForRecipe(data.spectrumMixList);
                setLightRecipe(data.lightRecipe);
            });
    }, [lightRecipeId]);

    React.useEffect(() => {
        if (selectedSpectrumMix && selectedSpectrumMix.id !== spectrumMixId) {
            onSpectrumMixSelect(lightRecipeId, selectedSpectrumMix.id);
        }
    }, [selectedSpectrumMix, onSpectrumMixSelect]);

    React.useEffect(() => {
        if (spectrumMixId) {
            const selected = matchedSpectrumMixList.find((e) => e.id === spectrumMixId);
            if (selected) {
                setSelectedSpectrumMix(selected);
            }
        }
    }, [matchedSpectrumMixList]);

    React.useEffect(() => {
        setMatchedSpectrumMixList(spectrumMixListForRecipe.filter((e) => e.groupId === formData.groupId));
    }, [formData.groupId, spectrumMixListForRecipe]);

    if (!lightRecipe) {
        return null;
    }

    function onClickSpectrumMixAlert() {
        history.push('/spectrum-mixes');
    }

    function getOptionLabel(option: SpectrumMixOption) {
        return option.name + (option.description ? ` (${option.description})` : '');
    }

    function isOptionEqualToValue(option: SpectrumMixOption, value: SpectrumMixOption) {
        return option.id === value.id;
    }

    function getOptionDisabled(option: SpectrumMixOption) {
        if (option.linked && option.linked.projectId !== projectId) {
            option.linkedReason = i18n('project_linked_spectrum_mix', option.linked.projectName) as string;
            return true;
        }

        return false;
    }

    function onChange(_event: React.SyntheticEvent, item: SpectrumMixOption | null) {
        setSelectedSpectrumMix(item);
        if (item === null && spectrumMixId !== null) {
            onSpectrumMixSelect(lightRecipeId, null);
        }
    }

    function renderInput(params: object) {
        return <TextField {...(params as object)} label={i18n('project_editor_select_spectrum_mix', lightRecipe!.name)} variant="outlined" />;
    }

    function renderOption(props: React.HTMLAttributes<HTMLLIElement>, option: SpectrumMixOption) {
        return (
            <li {...props}>
                <AutocompleteOptionWithChart option={option} spectrogramData={aggregateSpectrograms(option.lightChannels, option.lightRecipe)} size="small" />
            </li>
        );
    }

    const selectorOrAlert = matchedSpectrumMixList.length ? (
        <AutocompleteBordered
            value={selectedSpectrumMix}
            options={matchedSpectrumMixList}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionDisabled={getOptionDisabled}
            onChange={onChange}
            renderInput={renderInput}
            renderOption={renderOption}
        />
    ) : (
        <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            There is no Spectrum Mix for the <b>{lightRecipe.name}</b> Light Recipe.
            <Box>
                <Button style={{ marginTop: '10px' }} variant="contained" onClick={onClickSpectrumMixAlert}>
                    {i18n('add_new_spectrum_mix_button')}
                </Button>
            </Box>
        </Alert>
    );

    return (
        <Paper sx={{ pb: 2 }} elevation={9}>
            <Container>
                <Grid container spacing={3}>
                    <Grid item xs={5}>
                        <Typography variant="body2">{i18n('original_light_recipe')}</Typography>
                        <AutocompleteOptionWithChart option={lightRecipe} spectrogramData={lightRecipe.spectrogram} size="small" />
                    </Grid>
                    <Grid item xs={1}>
                        {selectedSpectrumMix ? <ArrowForwardIcon /> : null}
                    </Grid>
                    <Grid item xs={6}>
                        {selectedSpectrumMix ? (
                            <>
                                <Typography variant="body2">{i18n('mixed_spectrum')}</Typography>
                                <AutocompleteOptionWithChart
                                    option={selectedSpectrumMix}
                                    spectrogramData={aggregateSpectrograms(selectedSpectrumMix.lightChannels)}
                                    size="small"
                                />
                            </>
                        ) : null}
                    </Grid>
                    <Grid item xs={12}>
                        {readOnly ? null : selectorOrAlert}
                    </Grid>
                </Grid>
            </Container>
        </Paper>
    );
};

export default ProjectEditorSpectrumMixSelector;
